/**
 * clasificadores/index.js — Registry de clasificadores por dashboard
 *
 * Cada dashboard tiene una función clasificadora que:
 *   1. Recibe datos crudos de Firestore
 *   2. Devuelve secciones clasificadas con items accionables
 *
 * El agente busca su clasificador por dashboardId y lo ejecuta.
 * Para agregar un dashboard nuevo: crear su clasificador y registrarlo acá.
 *
 * Formato de cada entrada:
 *   dashboardId → {
 *     clasificar: async (empresaId) => { secciones, analisis, ... }
 *     // O la función pura si los datos se pasan desde afuera
 *   }
 */

import { clasificarPublicidad } from './publicidad'
import { clasificarPreguntasRespuestas } from './preguntasRespuestas'
import { clasificarOpiniones } from './opiniones'
import { clasificarPostventa } from './postventa'
import { clasificarVelocidadConsumo, COOLDOWN_DIAS } from './velocidadConsumo'
import { clasificarContribucionNeta } from './contribucionNeta'
import { clasificarPublicaciones } from './publicaciones'
import { clasificarCatalogo } from './catalogo'
import { clasificarMatrizDistribucion } from './matrizDistribucion'
import { clasificarEstrategiaFull } from './estrategiaFull'
import { clasificarEstrategiaFlex } from './estrategiaFlex'
import { clasificarCentralPromos } from './centralPromos'
import { clasificarTendenciasTareas } from './tendencias'
import { clasificarEvolucionMatriz } from './evolucionMatriz'
import { clasificarTraficoConversion } from './traficoConversion'
import { clasificarRentabilidadInferida } from './rentabilidadInferida'
import { clasificarProductosSinDatos } from './productosSinDatos'

// ═══════════════════════════════════════════════════════════════
// Registry: dashboardId → { clasificar, cargarYClasificar }
// ═══════════════════════════════════════════════════════════════

const CLASIFICADORES = {
  'ml-publicidad': {
    clasificar: clasificarPublicidad,
    // productoBitacora (10-jul-2026): cuarentena por condiciones — el agente
    // no re-sugiere activar un ad pausado sin cambio material posterior.
    // productos + catalogoLinks: tiempoReposicionDias para quebreStock.
    // 15-jul: + cascada ADR 032 para el corte del gate de ads (potencial).
    colecciones: ['mlAds', 'mlPublicaciones', 'mlCampanasAds', 'productoBitacora', 'productos', 'catalogoLinks', 'categorias', 'marcas', 'proveedores', 'productoSourcing', 'rentabilidadEmpresa'],
    diasVentas: 30,
    /** Secciones que el clasificador puede devolver. El admin tilda cuáles
     *  quiere que el agente procese. Si está vacío, no se generan tareas.
     *
     *  REGLA (Mariano, 10-jul-2026): todo filtro que genera tareas en la
     *  vista "🎯 Detección de tareas" del dashboard TIENE que poder correr
     *  automático — los 7 filtros de Publicidad están acá. Las secciones
     *  informativas (rendimiento — lo cubre la Matriz — y generadores
     *  indirectos — filtro de la Matriz) no generan tareas en ningún flujo. */
    seccionesDisponibles: [
      { id: 'activar',     label: '▶ Activar ads con buen rendimiento pausadas (respeta cuarentena post-pausa)' },
      { id: 'pausar',      label: '⏸ Pausar ads con gasto sin venta / pérdida (respeta piso de clicks y generadores protegidos)' },
      { id: 'pausarRent',  label: '🩸 Pausar ads con productos que generan pérdida (margen neto < 0)' },
      { id: 'quebreStock', label: '📦 Pausar ads con stock por quebrar (antes de la reposición)' },
      { id: 'optimizar',   label: '🔍 Revisar competitividad — ads con bajo rendimiento (ACOS alto, tanques)' },
      { id: 'revisar',     label: '🔍 Revisar competitividad — pausadas con buen CTR sin venta' },
      { id: 'potencial',   label: '🚀 Crear Ad — publicaciones con potencial sin publicidad' },
    ],
  },

  'ml-preguntas-respuestas': {
    clasificar: clasificarPreguntasRespuestas,
    colecciones: ['kbItems', 'mlPublicaciones', 'dashPreguntasMetricas'],
    diasVentas: 0,
    seccionesDisponibles: [
      { id: 'mejorar_publicacion', label: '📦 Mejorar publicación (hallazgos IA)' },
      { id: 'atender_horario',     label: '⚙️ Atender horario sub-óptimo' },
    ],
  },

  'ml-opiniones': {
    clasificar: clasificarOpiniones,
    colecciones: ['kbOpinionesItems', 'mlPublicaciones', 'dashOpinionesMetricas'],
    diasVentas: 0,
    seccionesDisponibles: [
      { id: 'mejorar_publicacion',         label: '📦 Mejorar publicación (destacar elogios + contrarrestar quejas)' },
      { id: 'atender_quejas_recurrentes',  label: '⚠️ Atender quejas recurrentes (rating bajo)' },
    ],
  },

  'ml-postventa': {
    clasificar: clasificarPostventa,
    necesitaMapeoReason: true, // carga mapeoReasonCategoria (global) para "otras problemáticas"
    // El evaluador necesita reclamos + devoluciones + órdenes + envíos + publicaciones.
    // Defaults sembrados en /agentes/agente-postventa-ml.umbrales.
    colecciones: ['mlReclamos', 'mlDevoluciones', 'mlOrdenes', 'mlEnvios', 'mlPublicaciones'],
    diasVentas: 30,
    seccionesDisponibles: [
      { id: 'modificar_publicacion', label: '📦 Modificar publicación (todas con reclamos)' },
      { id: 'pausar_publicacion',    label: '⏸ Pausar publicación problemática' },
      { id: 'cambiar_transportista', label: '🚚 Cambiar transportista (zona con quejas)' },
      { id: 'cambiar_proveedor',     label: '🏭 Cambiar proveedor (defectos)' },
      { id: 'cambiar_embalaje',      label: '📦 Cambiar embalaje (devoluciones damaged)' },
      // 14-jul-2026 (censo): el clasificador SIEMPRE emitió esta sección y el
      // agente la tilda — faltaba acá (regla central: TODOS los filtros que
      // generan tareas tienen que estar listados para el form del admin).
      { id: 'otras_problematicas',   label: '❓ Otras problemáticas (motivos ambiguos → tarea-resumen)' },
    ],
  },

  'ml-velocidad-consumo': {
    clasificar: clasificarVelocidadConsumo,
    // categorias/marcas/proveedores/productoSourcing/rentabilidadEmpresa
    // (12-jul): GATE DE RENTABILIDAD en activar_promo_sobrado — mismo criterio
    // que Full (cascada ADR 032): sin espacio NO se sugiere promo (declarado).
    // 15-jul: gate INFERIDO — fórmula completa (cuenta→ecosistema + impuestos)
    // + gasto REAL de ads (gatePromoConCostosReales); ads gastando sin vender
    // → bloqueada con motivo; zombies se cancelan retroactivo (gateBloqueados).
    // productoBitacora + cooldownBitacoraDias: el mismo cooldown que la vista
    // (una sobrada que ya tuvo promo/ad en la ventana no se re-sugiere).
    colecciones: ['mlPublicaciones', 'mlAds', 'mlPublicacionesVisitas', 'productos', 'catalogoLinks', 'categorias', 'marcas', 'proveedores', 'productoSourcing', 'rentabilidadEmpresa', 'productoBitacora'],
    diasVentas: 30,
    cooldownBitacoraDias: COOLDOWN_DIAS,
    seccionesDisponibles: [
      { id: 'reponer_quiebre',       label: '💀 Reposición urgente (stock = 0)' },
      { id: 'reponer_velocidad',     label: '📦 Reponer stock (cobertura crítica/baja)' },
      { id: 'activar_promo_sobrado', label: '🏷 Activar promoción (sobrestockeadas con tráfico, con gate de rentabilidad)' },
      { id: 'activar_ad_sobrado',    label: '⚡ Activar publicidad (sobrestockeadas sin visibilidad)' },
    ],
  },

  'ml-contribucion-neta': {
    clasificar: clasificarContribucionNeta,
    // ventas30d trae `ordenes` canónica por ventana (loader). El resto se carga
    // por whereEmpresaId. categorias se usa para resolver ivaPct por categoría
    // — igual que el dashboard (mayo 2026). (Este comentario prometía un
    // cooldown 30d por `productoBitacora`: el clasificador no recibe esa
    // colección —no está en `colecciones` ni en su firma— y un test de
    // caracterización asserta que pasársela no cambia nada.)
    // 12-jul-2026 (rework acción única): el módulo tiene UNA palanca, el
    // precio. El gate ADR 032 (marcas/proveedores/productoSourcing/
    // rentabilidadEmpresa) resuelve el margen mínimo por cascada — sin corte
    // el ítem no se toma. Pausar ad/promo viven en Publicidad y Central de
    // Promociones. categorias resuelve el IVA de la cascada de rentabilidad.
    colecciones: ['mlPublicaciones', 'mlAds', 'productos', 'catalogoLinks', 'categorias', 'marcas', 'proveedores', 'productoSourcing', 'rentabilidadEmpresa'],
    diasVentas: 30,
    // Paridad de universo con el dashboard (12-jul-2026): solo facturables.
    soloFacturables: true,
    seccionesDisponibles: [
      { id: 'subir_precio',   label: '📈 Subir precio (vende a pérdida — al margen mínimo)' },
      { id: 'mejorar_margen', label: '📊 Mejorar margen (bajo el mínimo aceptado, sin pérdida)' },
    ],
  },

  'ml-publicaciones': {
    clasificar: clasificarPublicaciones,
    // campanasCuotas: universo categoría × listing_type (ADR 012) para el
    // grupo 'disp' de cuotas — lo carga el loader genérico por empresaId.
    colecciones: ['mlPublicaciones', 'campanasCuotas'],
    diasVentas: 0,
    seccionesDisponibles: [
      { id: 'agregar_cuotas',  label: '💳 Agregar versión de cuotas (categoría permite, producto sin cuotas; ordenado por visitas)' },
      { id: 'mejorar_calidad', label: '🩺 Mejorar calidad de publicación (score < umbral, default 80)' },
      { id: 'modificar_foto',             label: '🖼 Mejorar foto — CON tráfico (quick win, ordenado por visitas)' },
      { id: 'modificar_foto_sin_trafico', label: '🖼 Mejorar foto — sin tráfico (backlog, prioridad baja; ordenado por precio)' },
    ],
  },

  'ml-catalogo': {
    clasificar: clasificarCatalogo,
    // catalogoListas: sublistas PRECALCULADAS del rollup del advisor de buy-box
    // (dashboardRollups/{empresaId}__catalogo/listas/{estado}) — loader propio,
    // el clasificador no recalcula el motor (calcular→persistir→mostrar).
    colecciones: ['catalogoListas'],
    diasVentas: 0,
    seccionesDisponibles: [
      { id: 'bajar_precio_ganar', label: '💰 Bajar precio para ganar buy-box (conviene: margen sobre el corte)' },
      { id: 'revisar_al_limite',  label: '🔍 Revisar competitividad (al límite del corte)' },
      { id: 'revisar_palancas',   label: '🧩 Palancas — no conviene ganar por precio (backlog, prioridad baja)' },
    ],
  },

  'ml-matriz-distribucion': {
    clasificar: clasificarMatrizDistribucion,
    // ventas30d llega por diasVentas (ordenes de la ventana). mlAds refuerza
    // la exclusión de publicitar (ad inscripto, cualquier estado). Piso de
    // tráfico configurable: empresa.umbralesMatriz.pisoVisitas (default 100).
    // 12-jul: completar_datos SALIÓ (decisión Mariano) — la siembra queda en
    // los botones "Sembrar" de las cards de la Matriz.
    // productos..rentabilidadEmpresa (12-jul): GATE DE RENTABILIDAD en
    // promocionar — mismo criterio que Full (cascada ADR 032): sin espacio
    // NO se sugiere promo (declarado en excluidos.sinEspacioRentabilidad).
    // 15-jul: gate INFERIDO — fórmula completa (cuenta→ecosistema + impuestos)
    // + gasto REAL de ads (gatePromoConCostosReales); ads gastando sin vender
    // → bloqueada con motivo; zombies se cancelan retroactivo (gateBloqueados).
    colecciones: ['mlPublicaciones', 'mlAds', 'productos', 'catalogoLinks', 'categorias', 'marcas', 'proveedores', 'productoSourcing', 'rentabilidadEmpresa'],
    diasVentas: 30,
    seccionesDisponibles: [
      { id: 'promocionar', label: '🏷 Promocionar — tráfico sin venta (incógnitas con visitas, sin promo, con gate de rentabilidad)' },
      { id: 'publicitar',  label: '⚡ Publicitar — sin ventas, sin tráfico, sin promo ni ad (el rincón muerto)' },
    ],
  },

  'ml-descarga-full': {
    clasificar: clasificarEstrategiaFull,
    // catalogoFull (tarifas vigentes, plataforma) y rentabilidadEmpresa
    // (empresas/{id}/config/rentabilidad) tienen casos propios en el loader;
    // categorias/marcas/proveedores/productoSourcing van por el genérico.
    colecciones: ['mlPublicaciones', 'mlAds', 'mlPublicacionesVisitas', 'productos', 'catalogoLinks', 'categorias', 'marcas', 'proveedores', 'productoSourcing', 'rentabilidadEmpresa', 'catalogoFull'],
    diasVentas: 30,
    seccionesDisponibles: [
      { id: 'enviar_full',       label: '📤 Enviar a Full — candidatos con ventas fuera de Full (sin rotación baja)' },
      { id: 'reponer_full',      label: '🔄 Reponer en Full — sin stock o quiebra antes del R total' },
      { id: 'publicitar_lento',  label: '⚡ Publicitar lento — sin tráfico y sin ad (escalera, paso 1)' },
      { id: 'promocionar_lento', label: '🏷 Promocionar lento — con tráfico, sin promo, con espacio de rentabilidad (paso 2)' },
      { id: 'retirar_full',      label: '📥 Retirar de Full — ciclo ≥45d pagando storage o palancas agotadas (paso final)' },
    ],
  },

  'ml-flex-zonas': {
    clasificar: clasificarEstrategiaFlex,
    // mlFlexConfig tiene caso propio en el loader (elige el doc per-conexión
    // con coverage.byZone más reciente). Las ventas van por la ventana (_ventas).
    colecciones: ['mlFlexConfig'],
    diasVentas: 30,
    seccionesDisponibles: [
      { id: 'activar_zona',        label: '🗺 Activar zona — apagadas con ventas (las zonas son las de ML)' },
      { id: 'ampliar_horario',     label: '📅 Ampliar horario — órdenes entre el cutoff ML y el tope del operador (⚙️ por zona)' },
      { id: 'revisar_performance', label: '🚨 Revisar performance — is_downgraded de ML (gestión)' },
    ],
  },

  'ml-central-promociones': {
    clasificar: clasificarCentralPromos,
    // Todas las colecciones tienen caso propio en el loader (canónicas →
    // shape legacy via centralPromosCanonicoShape): mlPromos (campanas
    // tipo='promo'), mlPublicacionesPromos (campanasItems sin ads),
    // mlPublicacionesCentral (publicaciones + familyId), eventos del
    // calendario comercial (plataforma, paisId AR) y las Reglas de Decisión
    // central-promociones (cascada empresa → global → defaults).
    // productos/catalogoLinks van por sus casos existentes. diasVentas: 90
    // — el perfil de venta histórico usa la misma ventana que el dashboard.
    // El motor ya decide por margen con Reglas de Decisión → SIN gate
    // genérico de rentabilidad acá.
    // ACCIÓN DOBLE (12-jul-2026): sumarse_apagando_publi emite SOLO la mitad
    // 1 (pausar_ad); la mitad 2 (activar_promo) sale de la re-detección
    // cuando la publi ya quedó apagada.
    colecciones: ['mlPromos', 'mlPublicacionesPromos', 'mlPublicacionesCentral', 'productos', 'catalogoLinks', 'catalogoEventosCalendario', 'reglasCentralPromos'],
    diasVentas: 90,
    seccionesDisponibles: [
      { id: 'sumarse_promo',          label: '🟢 Sumarse — candidatas con margen neto post-promo sobre el umbral' },
      { id: 'promo_propia',           label: '🟢 Promo propia — pubs sin promo ML con margen para el descuento default' },
      { id: 'sumarse_apagando_publi', label: '🟡 Sumarse apagando publi — mitad 1: pausar la publi (la re-detección emite el sumarse)' },
      { id: 'apagar_publi_promo',     label: '🟡 Apagar publi en promo — participando, la publi se come el margen' },
      { id: 'bajarse_promo',          label: '🔴 Bajarse — participando y no cierra ni apagando publi (o stock crítico)' },
    ],
  },

  'ml-tendencias': {
    clasificar: clasificarTendenciasTareas,
    // publicacionesTendencias (pubs canónicas RAW) y refTendencias (post-step
    // que llama al callable resolverTendencias cache-first, igual que el
    // dashboard) tienen casos propios en el loader. tendenciasVeredictosTitulo
    // (cache del gate de compatibilidad IA) y marcas van por el genérico
    // whereEmpresaId. Sin ventana de ventas (el motor cruza títulos, no órdenes).
    // GATE DE COMPATIBILIDAD (12-jul-2026): las secciones de TÍTULO solo
    // emiten ítems con veredicto IA aplica=true (o marca propia) — marca ajena
    // ("cafe martinez") y atributos que no aplican ("corpiño lactancia") se
    // vetan; sin veredicto quedan pendientes (la vista los verifica con IA).
    // Las secciones EXACTAS no pasan por el gate (el título ya contiene la
    // búsqueda). keywordsExcluidas (empresa.tendenciasConfig) = curación manual.
    colecciones: ['publicacionesTendencias', 'refTendencias', 'tendenciasVeredictosTitulo', 'marcas'],
    diasVentas: 0,
    seccionesDisponibles: [
      { id: 'reponer_tendencia', label: '🔥 Reponer (tendencia) — búsqueda exacta en el título y sin stock' },
      { id: 'buybox_tendencia',  label: '🔥 Buy-box (tendencia) — búsqueda exacta y perdiendo la caja (ordenado por vendidas)' },
      { id: 'titulo_amplia',     label: '✏️ Optimizar título — amplia → exacta (solo pares verificados por IA; ordenado por demanda de la keyword)' },
      { id: 'titulo_sin',        label: '✏️ Optimizar título — sin concordancia, sumar keyword del rubro (verificados; ordenado por precio)' },
    ],
  },

  'ml-trafico-conversion': {
    // Clasificador PURO envuelto: mapea el `datos` del loader al input de
    // clasificarTraficoConversion (mismas medianas y grupos que la vista 🎯).
    //   publicaciones: pubCanonicaALegacy (trae _visitas30d + facetas.conAd)
    //   ads:           adsItems (mlAds) → refExternalId + metricas.clicks
    //   ventas:        ordenes de la ventana (soloFacturables, paridad dashboard)
    clasificar: ({ publicaciones = [], adsItems = [], ventas30d = [], config = {}, ecosistema = null, impuestosEmpresa = null, productos = [], catalogoLinks = [], categorias = [], marcas = [], proveedores = [], productoSourcing = [], rentabilidadEmpresa = null }) => {
      // Unidades facturables por publicación desde order_items crudos ML.
      const ventasPorItem = {}
      for (const o of (ventas30d || [])) {
        for (const it of (o?.order_items || [])) {
          const mla = String(it?.item?.id ?? it?.item_id ?? '')
          if (!mla) continue
          ventasPorItem[mla] = (ventasPorItem[mla] || 0) + Number(it?.quantity ?? 0)
        }
      }
      // Ads al shape que consume el clasificador (solo clicks importan para conAd).
      const ads = (adsItems || []).map((a) => ({
        refExternalId: String(a?.item_id ?? a?.refExternalId ?? ''),
        metricas: {
          clicks: Number(a?.metrics?.clicks ?? 0),
          units_quantity: Number(a?.metrics?.units_quantity ?? 0),
        },
      }))
      return clasificarTraficoConversion({ publicaciones, ads, ventasPorItem, config, ecosistema, impuestosEmpresa, productos, catalogoLinks, categorias, marcas, proveedores, productoSourcing, rentabilidadEmpresa })
    },
    // 15-jul: + cascada ADR 032 para el corte del gate de ads.
    colecciones: ['mlPublicaciones', 'mlAds', 'productos', 'catalogoLinks', 'categorias', 'marcas', 'proveedores', 'productoSourcing', 'rentabilidadEmpresa'],
    diasVentas: 30,
    // Paridad de universo con el dashboard: ventas = solo órdenes facturables.
    soloFacturables: true,
    seccionesDisponibles: [
      { id: 'potenciar',   label: '⚡ Potenciar — CVR alto sin ads (crear ad para traer tráfico)' },
      { id: 'sin_trafico', label: '👻 Sin tráfico — activas sin ads y sin visitas (crear ad para visibilizar)' },
      { id: 'revisar_cvr', label: '🩺 CVR bajo — mucho tráfico que convierte mal (revisar competitividad)' },
    ],
  },

  'ml-evolucion-matriz': {
    clasificar: clasificarEvolucionMatriz,
    // La película, no la foto: serie mensual de cuadrantes (meses COMPLETOS —
    // el mes en curso parcial no fabrica caídas) con el mismo detectarPatron
    // del dashboard. diasVentas 215 cubre los 6 meses completos default.
    // Las caídas con stock 0 se DECLARAN (quiebre → Velocidad repone);
    // despertando excluye pubs con ad inscripto (cualquier estado, criterio
    // Matriz); estrella con margen negativo declarada (Contribución Neta).
    // 15-jul: + cascada ADR 032 para el corte del gate de ads (despertar).
    colecciones: ['mlPublicaciones', 'mlAds', 'productos', 'catalogoLinks', 'categorias', 'marcas', 'proveedores', 'productoSourcing', 'rentabilidadEmpresa'],
    diasVentas: 215,
    seccionesDisponibles: [
      { id: 'caida_matriz',     label: '⚠️ En caída — venía bien y cae de cuadrante (sin quiebres de stock)' },
      { id: 'cronica_matriz',   label: '🪦 Crónica — eventual/incógnita todos los meses (capital atrapado primero)' },
      { id: 'despertar_matriz', label: '🚀 Despertando — sube sola y sin publicidad (acelerar con ad)' },
    ],
  },

  'ml-rentabilidad-inferida': {
    clasificar: clasificarRentabilidadInferida,
    // Dashboard VIOLETA (15-jul-2026): universo DISJUNTO de Contribución Neta
    // — pubs ACTIVAS SIN venta en la ventana; el margen se INFIERE (fórmula
    // completa cuenta→ecosistema + impuestos, lib rentabilidadEstimada).
    // Decisión Mariano: las dos secciones emiten la MISMA acción
    // (subir_precio al precio sugerido) en dos grupos por urgencia.
    // Guardas: solo costo propio del producto emite (sin costo → declarado);
    // histéresis 3 pts en bajo_minima (umbrales.histeresisPts); ads gastando
    // sin vender declarado (pausar_ad lo trabaja Publicidad — capa 1).
    // mlAds trae el join campanasItems⨝campanasMetricas (gasto real).
    // con_espacio y sin_costo son informativas — no generan tareas.
    colecciones: ['mlPublicaciones', 'mlAds', 'productos', 'catalogoLinks', 'categorias', 'marcas', 'proveedores', 'productoSourcing', 'rentabilidadEmpresa'],
    diasVentas: 30,
    seccionesDisponibles: [
      { id: 'perdida_inferida',     label: '🩸 Subir precio — margen inferido NEGATIVO (a pérdida sin haber vendido; URGENTE)' },
      { id: 'bajo_minima_inferida', label: '⚠️ Subir precio — margen inferido bajo la rentabilidad mínima (con histéresis)' },
    ],
  },

  'productos-cargar-datos': {
    clasificar: clasificarProductosSinDatos,
    // El INSUMO del flywheel (16-jul-2026): productos activos con vínculo ML
    // sin costo y/o tiempo de reposición → UNA tarea de carga con el CSV
    // pre-llenado ADJUNTO (buildAdjuntosAgente de la sección, inline en la
    // tarea). Capa 0: en el agente general corre PRIMERO — y el auto-seed
    // (paso 0 del general) le alimenta el universo con los productos recién
    // sembrados en la misma corrida. No es un dashboard: la vista manual es
    // el banner de Empresa → Productos (misma sección, mismo dedupe).
    colecciones: ['productos', 'catalogoLinks', 'categorias', 'marcas'],
    diasVentas: 0,
    seccionesDisponibles: [
      { id: 'productos_sin_datos', label: '🧾 Cargar costo/reposición — productos con vínculo ML sin datos (CSV adjunto)' },
    ],
  },
}

/**
 * Obtener el clasificador para un dashboard
 * @param {string} dashboardId
 * @returns {Object|null} { clasificar, colecciones, diasVentas }
 */
export function getClasificador(dashboardId) {
  return CLASIFICADORES[dashboardId] || null
}

/**
 * Verificar si un dashboard tiene clasificador registrado
 * @param {string} dashboardId
 * @returns {boolean}
 */
export function tieneClasificador(dashboardId) {
  return dashboardId in CLASIFICADORES
}

/**
 * Listar todos los dashboards con clasificador
 * @returns {string[]} array de dashboardIds
 */
export function dashboardsConClasificador() {
  return Object.keys(CLASIFICADORES)
}

/**
 * Obtener las secciones disponibles para un dashboardId.
 * Las usa el form de creación de agente para que el admin tilde cuáles
 * procesa el agente (en lugar de tipearlas a mano).
 *
 * @param {string} dashboardId
 * @returns {Array<{id: string, label: string}>}
 */
export function getSeccionesDisponibles(dashboardId) {
  const def = CLASIFICADORES[dashboardId]
  if (!def || !Array.isArray(def.seccionesDisponibles)) return []
  return def.seccionesDisponibles
}
